export const normalizeMacAddress = (value) =>
  String(value || "").replace(/\\/g, "").trim().toUpperCase();

export const formatMacAddressInput = (value) => {
  const hex = String(value || "").replace(/[^0-9a-f]/gi, "").slice(0, 12).toUpperCase();
  return hex.match(/.{1,2}/g)?.join(":") || "";
};

export const isValidMacAddress = (value) =>
  /^([0-9A-F]{2}:){5}[0-9A-F]{2}$/.test(normalizeMacAddress(value));

const numberFrom = (row, keys) => {
  for (const key of keys) {
    const value = Number(row?.[key]);
    if (Number.isFinite(value)) return value;
  }
  return 0;
};

const stringFrom = (row, keys) => {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== undefined && value !== null && String(value).trim()) return String(value).trim();
  }
  return "";
};

const expandCameraServiceRows = (source) =>
  source.flatMap((row) => {
    if (!Array.isArray(row?.records)) return [row];
    if (!row.records.length) return [];

    const envelopeMetadata = {
      ...(row?.people_counter_id != null ? { people_counter_id: row.people_counter_id } : {}),
      ...(row?.people_counter_name ? { people_counter_name: row.people_counter_name } : {}),
      ...(row?.organization_id != null ? { organization_id: row.organization_id } : {}),
      ...(row?.organization_name ? { organization_name: row.organization_name } : {}),
      ...(row?.mac_address ? { mac_address: row.mac_address } : {}),
      ...(row?.macAddress ? { macAddress: row.macAddress } : {}),
      ...(row?.device_name ? { device_name: row.device_name } : {}),
      ...(!row?.device_name && row?.name ? { device_name: row.name } : {}),
      ...(row?.ipAddress != null ? { ipAddress: row.ipAddress } : {}),
      ...(row?.siteId != null ? { siteId: row.siteId } : {}),
    };

    return row.records.map((record) => ({
      ...envelopeMetadata,
      ...(record && typeof record === "object" ? record : {}),
    }));
  });

export const normalizePeopleCounterRows = (payload) => {
  const rawSource = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : [];
  const source = expandCameraServiceRows(rawSource);

  return source.map((row, index) => {
    const macAddress = normalizeMacAddress(stringFrom(row, [
      "mac_address", "macAddress", "mac", "device_mac", "device_sn", "deviceSn",
    ]));
    const windowStart = stringFrom(row?.timeWindow || {}, ["start"])
      || stringFrom(row, ["window_start", "startTime", "start_time"]);
    const windowEnd = stringFrom(row?.timeWindow || {}, ["end"])
      || stringFrom(row, ["window_end", "endTime", "end_time"]);
    // createdAt is the actual persisted reading timestamp on the single-device endpoint.
    // Prefer it over the camera time window because some devices currently report the Unix epoch there.
    const receivedAt = stringFrom(row, [
      "received_at", "receivedAt", "timestamp", "created_at", "createdAt", "time",
    ]) || windowEnd || windowStart;
    const trafficEntered = numberFrom(row?.traffic || {}, ["entered"]) || numberFrom(row, ["entered"]);
    const trafficExited = numberFrom(row?.traffic || {}, ["exited"]) || numberFrom(row, ["exited"]);
    const details = row?.details && typeof row.details === "object" ? row.details : {};
    return {
      ...row,
      _row_id: row?._row_id || `${macAddress || "reading"}-${receivedAt || index}-${index}`,
      people_counter_name: stringFrom(row, ["people_counter_name", "device_name", "name"]) || "-",
      mac_address: macAddress || "-",
      organization_name: stringFrom(row, ["organization_name", "organizationName"]) || "-",
      line: stringFrom(row, ["line"]) || "-",
      line_name: stringFrom(row, ["line_name", "lineName"]) || "-",
      people_in: numberFrom(row, ["people_in", "peopleIn", "in"]) || trafficEntered,
      people_out: numberFrom(row, ["people_out", "peopleOut", "out"]) || trafficExited,
      traffic_entered: trafficEntered,
      traffic_exited: trafficExited,
      total_in: numberFrom(row, ["total_in", "totalIn"]) || trafficEntered,
      total_out: numberFrom(row, ["total_out", "totalOut"]) || trafficExited,
      children_in: numberFrom(details, ["childrenIn"]) || numberFrom(row, ["childrenIn", "children_in"]),
      children_out: numberFrom(details, ["childrenOut"]) || numberFrom(row, ["childrenOut", "children_out"]),
      group_in: numberFrom(details, ["groupIn"]) || numberFrom(row, ["groupIn", "group_in"]),
      group_out: numberFrom(details, ["groupOut"]) || numberFrom(row, ["groupOut", "group_out"]),
      staff_in: numberFrom(details, ["staffIn"]) || numberFrom(row, ["staffIn", "staff_in"]),
      staff_out: numberFrom(details, ["staffOut"]) || numberFrom(row, ["staffOut", "staff_out"]),
      window_start: windowStart || null,
      window_end: windowEnd || null,
      received_at: receivedAt || null,
    };
  });
};

const readingTime = (row) => {
  const value = new Date(row?.received_at || 0).getTime();
  return Number.isFinite(value) ? value : 0;
};

export const getLatestReadingByDevice = (rows) => {
  // A device can return several lines in one latest response. Keep the newest
  // reading for every device+line pair; collapsing only by MAC would silently
  // drop line2, line3, ... from totals and charts.
  const latest = new Map();
  rows.forEach((row, index) => {
    const deviceKey = normalizeMacAddress(row.mac_address) || String(row.people_counter_id || row._row_id);
    const lineKey = row?.line !== undefined && row?.line !== null && row?.line !== "-"
      ? String(row.line)
      : row?.line_name && row.line_name !== "-"
        ? String(row.line_name)
        : `row-${index}`;
    const key = `${deviceKey}::${lineKey}`;
    const current = latest.get(key);
    if (!current || readingTime(row) >= readingTime(current)) latest.set(key, row);
  });
  return [...latest.values()];
};

export const buildPeopleCounterSummary = (rows, registeredDeviceCount = 0) => {
  const latestRows = getLatestReadingByDevice(rows);
  const reportingDevices = new Set(
    latestRows.map((row) => normalizeMacAddress(row.mac_address) || String(row.people_counter_id || ""))
      .filter(Boolean)
  ).size;
  const latestEntered = latestRows.reduce((sum, row) => sum + Number(row.total_in || row.people_in || 0), 0);
  const latestExited = latestRows.reduce((sum, row) => sum + Number(row.total_out || row.people_out || 0), 0);
  return {
    registeredDevices: Number(registeredDeviceCount || 0),
    reportingDevices,
    peopleIn: rows.reduce((sum, row) => sum + Number(row.people_in || 0), 0),
    peopleOut: rows.reduce((sum, row) => sum + Number(row.people_out || 0), 0),
    currentOccupancy: Math.max(0, latestEntered - latestExited),
  };
};

export const buildPeopleCounterChartData = (rows) => {
  const grouped = new Map();
  getLatestReadingByDevice(rows).forEach((row) => {
    const key = normalizeMacAddress(row.mac_address) || String(row.people_counter_id || row._row_id);
    const current = grouped.get(key) || {
      name: row.people_counter_name || row.mac_address,
      peopleIn: 0,
      peopleOut: 0,
    };
    current.peopleIn += Number(row.total_in || row.people_in || 0);
    current.peopleOut += Number(row.total_out || row.people_out || 0);
    grouped.set(key, current);
  });
  return [...grouped.values()].slice(0, 20);
};
